import {Icon} from '@atoms';
import {useBreakPoint, useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {ConnectionUser, getAllConnectedUsers, getIsLoadingConnections, useAppSelector} from '@lib/store';
import {Avatar} from '@molecules';
import {FlexCol, FlexRow, H3, H4, Subtitle} from '@quarks';
import {cn} from '@utils';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {useConnectionsSchedule} from '@lib/store/src/ui/hooks/timeslots/useConnectionsSchedule';
import {constructConnectionsOverview} from '@lib/store/src/data/timeslots/viewmodel-helpers/constructConnectionsOverview';
import {ConnectionLocation, ConnectionOnMapModal} from './ConnectionOnMapModal';
import {NoConnections, NoOneThere} from './EmptyState';
import {ConnectionsLoader} from './ConnectionsLoader';
import {ModalPage} from '../../ModalPage';

export const ConnectionOverview = ({
  dateKey,
  asBigPage,
  hideTitle,
  buildingId,
}: {
  dateKey: string;
  asBigPage?: boolean;
  hideTitle?: boolean;
  buildingId?: string;
}) => {
  const bp = useBreakPoint();
  const {t} = useTranslation();

  const {setBigPages, addPages, openModal, closeModal} = useModal();

  const showConnectionOnMap = (user: ConnectionUser, location: ConnectionLocation) => {
    const mapView = (
      <ConnectionOnMapModal
        user={user}
        workspace={location}
      />
    );

    trackEvent('Home_ConnectionsOverview__ExpandMap');

    addPages([mapView]);
    if (asBigPage) setBigPages([mapView]);
    openModal();
  };

  const connectionsSchedule = useConnectionsSchedule(dateKey);
  const connectionsLoading = useAppSelector(getIsLoadingConnections);
  const connectedUsers = useAppSelector(getAllConnectedUsers);
  const overview = constructConnectionsOverview(connectedUsers, connectionsSchedule.workspaceResevations);

  const connectionsForBuilding = overview.filter((o) => {
    return !buildingId || o.occupants[0].reservations[0].workspace.buildingId === buildingId;
  });

  if (connectionsLoading || connectionsSchedule.status === 'pending') return <ConnectionsLoader />;

  return (
    <FlexCol
      gap={16}
      className="w-full">
      {!hideTitle && (
        <FlexRow>
          <H3 className="pl-0">{t('screen:AllConnections')}</H3>
          {overview.length > 1 && (
            <button
              className="text-collaborative-blue-500* font-semibold text-sm"
              onClick={() => {
                addPages([
                  <ModalPage
                    title="Connections at the office"
                    onClose={closeModal}>
                    <div className="py-4">
                      <ConnectionOverview
                        hideTitle
                        dateKey={dateKey}
                      />
                    </div>
                  </ModalPage>,
                ]);
                openModal();
              }}>
              {t('ViewAll')}
            </button>
          )}
        </FlexRow>
      )}

      {connectedUsers.length === 0 && <NoConnections />}
      {connectionsForBuilding.length === 0 && <NoOneThere />}
      {connectionsForBuilding.map((o) => (
        <Fragment key={o.locationName}>
          {!buildingId && <H4 className="pl-0">{o.locationName}</H4>}
          <div className={cn('grid full-w gap-6 grid-cols-1', {'grid-cols-2': bp === 'large'})}>
            {o.occupants.map(({user, reservations}) => {
              // TODO: this shows the first reservation of the day
              const reservation = reservations[0];
              const {workspace} = reservation;
              let workspaceLabel =
                'areaName' in workspace
                  ? `${workspace.floorName}, ${workspace.areaName}`
                  : 'floorName' in workspace
                  ? workspace.floorName
                  : '';

              if (reservations.length > 1) {
                workspaceLabel += ` (+${reservations.length - 1} more)`;
              }

              return (
                <div
                  className="flex w-full gap-2 items-center"
                  key={user.id}>
                  <Avatar
                    className="flex-none"
                    shouldShowCheckIn
                    user={user}
                  />
                  <div className="overflow-hidden mr-auto">
                    <p className="truncate">{user.name}</p>
                    {workspaceLabel && <Subtitle className="truncate">{workspaceLabel}</Subtitle>}
                  </div>
                  {workspace ? (
                    <button
                      className="flex-none"
                      aria-label={t('ShowOnMap')}
                      onClick={() => {
                        if ('areaName' in workspace) {
                          showConnectionOnMap(user, workspace);
                        }
                      }}>
                      <Icon
                        icon="map"
                        size="20px"
                        color="#767676"
                      />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Fragment>
      ))}
    </FlexCol>
  );
};
